import { useState, useEffect, useRef } from "react";
import { ArrowRight, Zap, MessageCircle, Brain, CalendarCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const timelineData = [
  {
    id: 1,
    title: "Mensagem recebida",
    date: "Passo 01",
    content: "Recepção instantânea no WhatsApp da sua empresa. A Automia identifica o cliente e recupera o histórico automaticamente.",
    category: "Receção",
    icon: MessageCircle,
    relatedIds: [2],
    status: "pending",
    energy: 50,
  },
  {
    id: 2,
    title: "IA analisa e responde",
    date: "Passo 02",
    content: "Consulta a base de conhecimento e os slots livres do Google Calendar. Responde em Português de Portugal, com o tom do seu negócio.",
    category: "Processamento",
    icon: Brain,
    relatedIds: [1, 3],
    status: "in-progress",
    energy: 75,
  },
  {
    id: 3,
    title: "Marcação",
    date: "Passo 03",
    content: "Se a confiança for alta, agenda. Se não for, pausa a IA e notifica-o no seu WhatsApp pessoal. O controlo é seu.",
    category: "Conclusão",
    icon: CalendarCheck,
    relatedIds: [2],
    status: "completed",
    energy: 100,
  },
];

function RadialOrbitalTimeline() {
  const [expandedItems, setExpandedItems] = useState({});
  const [rotationAngle, setRotationAngle] = useState(0);
  const [autoRotate, setAutoRotate] = useState(true);
  const [pulseEffect, setPulseEffect] = useState({});
  const [activeNodeId, setActiveNodeId] = useState(null);
  const containerRef = useRef(null);
  const orbitRef = useRef(null);
  const nodeRefs = useRef({});

  const handleContainerClick = (e) => {
    if (e.target === containerRef.current || e.target === orbitRef.current) {
      setExpandedItems({});
      setActiveNodeId(null);
      setPulseEffect({});
      setAutoRotate(true);
    }
  };

  const toggleItem = (id) => {
    setExpandedItems((prev) => {
      const newState = {};
      timelineData.forEach((item) => { newState[item.id] = false; });
      newState[id] = !prev[id];

      if (!prev[id]) {
        setActiveNodeId(id);
        setAutoRotate(false);
        const current = timelineData.find((i) => i.id === id);
        const newPulse = {};
        current?.relatedIds.forEach((relId) => { newPulse[relId] = true; });
        setPulseEffect(newPulse);
      } else {
        setActiveNodeId(null);
        setAutoRotate(true);
        setPulseEffect({});
      }

      return newState;
    });
  };

  useEffect(() => {
    if (!autoRotate) return;
    const timer = setInterval(() => {
      setRotationAngle((prev) => Number(((prev + 0.3) % 360).toFixed(3)));
    }, 50);
    return () => clearInterval(timer);
  }, [autoRotate]);

  const calculateNodePosition = (index, total) => {
    const angle = ((index / total) * 360 + rotationAngle) % 360;
    const radius = typeof window !== "undefined" && window.innerWidth >= 1024 ? 220 : 160;
    const radian = (angle * Math.PI) / 180;
    const x = radius * Math.cos(radian);
    const y = radius * Math.sin(radian);
    const zIndex = Math.round(100 + 50 * Math.cos(radian));
    const opacity = Math.max(0.4, Math.min(1, 0.4 + 0.6 * ((1 + Math.sin(radian)) / 2)));
    const labelBelow = y <= 20; // place label below when node is in upper half
    return { x, y, zIndex, opacity, labelBelow };
  };

  const isRelatedToActive = (itemId) => {
    if (!activeNodeId) return false;
    const active = timelineData.find((i) => i.id === activeNodeId);
    return active?.relatedIds.includes(itemId) ?? false;
  };

  const getStatusStyles = (status) => {
    switch (status) {
      case "completed": return "text-white bg-blue-600 border-blue-400";
      case "in-progress": return "text-white bg-cyan-600 border-cyan-400";
      default: return "text-slate-300 bg-white/10 border-white/20";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "completed": return "Concluído";
      case "in-progress": return "Em curso";
      default: return "Pendente";
    }
  };

  return (
    <div
      className="w-full h-[560px] lg:h-[700px] flex items-center justify-center relative"
      ref={containerRef}
      onClick={handleContainerClick}
    >
      <div
        className="absolute w-full h-full flex items-center justify-center"
        ref={orbitRef}
      >
        {/* Centre orb */}
        <div className="absolute w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 via-cyan-500 to-blue-700 animate-pulse flex items-center justify-center z-10">
          <div className="absolute w-18 h-18 rounded-full border border-blue-400/20 animate-ping opacity-60" />
          <div className="absolute w-22 h-22 rounded-full border border-blue-400/10 animate-ping opacity-40" style={{ animationDelay: "0.5s" }} />
          <div className="w-7 h-7 rounded-full bg-white/80 backdrop-blur-md" />
        </div>

        {/* Orbit ring */}
        <div className="absolute w-[336px] h-[336px] lg:w-[456px] lg:h-[456px] rounded-full border border-white/10" />

        {/* Nodes */}
        {timelineData.map((item, index) => {
          const position = calculateNodePosition(index, timelineData.length);
          const isExpanded = expandedItems[item.id];
          const isRelated = isRelatedToActive(item.id);
          const isPulsing = pulseEffect[item.id];
          const Icon = item.icon;
          const { labelBelow } = position;

          return (
            <div
              key={item.id}
              ref={(el) => (nodeRefs.current[item.id] = el)}
              className="absolute transition-all duration-700 cursor-pointer"
              style={{
                transform: `translate(${position.x}px, ${position.y}px)`,
                zIndex: isExpanded ? 200 : position.zIndex,
                opacity: isExpanded ? 1 : position.opacity,
              }}
              onClick={(e) => { e.stopPropagation(); toggleItem(item.id); }}
            >
              {/* Glow aura */}
              <div
                className={`absolute rounded-full ${isPulsing ? "animate-pulse" : ""}`}
                style={{
                  background: "radial-gradient(circle, rgba(59,130,246,0.25) 0%, transparent 70%)",
                  width: `${item.energy * 0.4 + 40}px`,
                  height: `${item.energy * 0.4 + 40}px`,
                  left: `-${(item.energy * 0.4 + 40 - 40) / 2}px`,
                  top: `-${(item.energy * 0.4 + 40 - 40) / 2}px`,
                }}
              />

              {/* Node button */}
              <div className={`
                w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-300
                ${isExpanded ? "bg-blue-500 text-white border-blue-300 shadow-lg shadow-blue-500/40 scale-150"
                  : isRelated ? "bg-white/20 text-white border-white animate-pulse"
                  : "bg-[#050B14] text-blue-300 border-white/30 hover:border-blue-400"}
              `}>
                <Icon size={15} />
              </div>

              {/* Label — above or below depending on orbit position */}
              <div className={`
                absolute left-1/2 -translate-x-1/2 whitespace-nowrap text-xs font-semibold tracking-wide transition-all duration-300
                ${labelBelow ? "top-12" : "bottom-12"}
                ${isExpanded ? "text-white scale-110" : "text-white/60"}
              `}>
                {item.title}
              </div>

              {/* Expanded card — opens up or down based on orbit position */}
              {isExpanded && (
                <Card className={`absolute left-1/2 -translate-x-1/2 w-64 bg-[#0a1628]/95 backdrop-blur-lg border-blue-500/30 shadow-xl shadow-blue-500/10 ${labelBelow ? "top-20" : "bottom-20"}`}>
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-px h-3 bg-blue-400/50" />
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-center">
                      <Badge className={`px-2 text-[10px] ${getStatusStyles(item.status)}`}>
                        {getStatusLabel(item.status)}
                      </Badge>
                      <span className="text-[10px] font-mono text-slate-500">{item.date}</span>
                    </div>
                    <CardTitle className="text-sm text-white mt-2">{item.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="text-xs text-slate-300">
                    <p>{item.content}</p>
                    <div className="mt-4 pt-3 border-t border-white/10">
                      <div className="flex justify-between text-xs mb-1 text-slate-400">
                        <span className="flex items-center gap-1"><Zap size={9} />Progresso</span>
                        <span className="font-mono">{item.energy}%</span>
                      </div>
                      <div className="w-full h-1 bg-white/10 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-blue-500 to-cyan-400" style={{ width: `${item.energy}%` }} />
                      </div>
                    </div>
                    {item.relatedIds.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-white/10">
                        <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Passos ligados</p>
                        <div className="flex flex-wrap gap-1">
                          {item.relatedIds.map((relId) => {
                            const rel = timelineData.find((i) => i.id === relId);
                            return (
                              <Button
                                key={relId}
                                variant="outline"
                                size="sm"
                                className="h-6 px-2 text-[10px] border-blue-500/30 bg-transparent text-blue-300 hover:bg-blue-500/10"
                                onClick={(e) => { e.stopPropagation(); toggleItem(relId); }}
                              >
                                {rel?.title}
                                <ArrowRight size={8} className="ml-1" />
                              </Button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function HowItWorks() {
  return (
    <section
      id="como-funciona"
      data-testid="how-it-works"
      className="relative py-16 sm:py-24 lg:py-32"
    >
      <div className="absolute inset-0 grid-pattern opacity-40" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-4">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass-card text-[11px] font-semibold uppercase tracking-[0.2em] text-blue-300">
            Como Funciona
          </div>
          <h2 className="mt-6 text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tighter text-white leading-[1.05]">
            Três passos. <span className="text-gradient-blue">Zero esforço.</span>
          </h2>
          <p className="mt-4 text-slate-400 text-lg">Em três passos, recupera horas da sua semana e recebe mais marcações, sem levantar um dedo.</p>
        </div>

        {/* Mobile: vertical steps */}
        <div className="lg:hidden mt-10 space-y-4">
          {timelineData.map((item, index) => {
            const Icon = item.icon;
            const statusColors = {
              completed: { bg: "bg-blue-600", border: "border-blue-400", text: "text-blue-300", label: "Concluído" },
              "in-progress": { bg: "bg-cyan-600", border: "border-cyan-400", text: "text-cyan-300", label: "Em curso" },
              pending: { bg: "bg-white/10", border: "border-white/20", text: "text-slate-400", label: "Pendente" },
            };
            const s = statusColors[item.status];
            return (
              <div key={item.id} className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 flex-shrink-0 ${s.bg} ${s.border}`}>
                    <Icon size={16} className="text-white" />
                  </div>
                  {index < timelineData.length - 1 && (
                    <div className="w-px flex-1 bg-white/10 mt-2" />
                  )}
                </div>
                <div className="pb-6 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] font-semibold uppercase tracking-widest ${s.text}`}>{item.date}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${s.bg} ${s.border} text-white`}>{s.label}</span>
                  </div>
                  <h3 className="text-base font-semibold text-white mb-1">{item.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{item.content}</p>
                  <div className="mt-3">
                    <div className="flex justify-between text-[10px] text-slate-500 mb-1">
                      <span>Progresso</span>
                      <span className="font-mono">{item.energy}%</span>
                    </div>
                    <div className="w-full h-1 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-blue-500 to-cyan-400" style={{ width: `${item.energy}%` }} />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Desktop: orbital */}
        <div className="hidden lg:block">
          <RadialOrbitalTimeline />
        </div>
      </div>
    </section>
  );
}
